import { useRouter, useRouterState } from "@tanstack/react-router";
import { createContext, useContext, useEffect, useRef } from "react";

type FocusReturn = { href: string; index: number };

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    layout?: "calculator-focus";
  }
}

function readFocusReturn(state: object): FocusReturn | undefined {
  if (!("calculatorReturn" in state)) return;
  const value = state.calculatorReturn;
  if (!value || typeof value !== "object" || !("href" in value) || !("index" in value)) return;
  if (
    typeof value.href === "string" &&
    value.href.startsWith("/") &&
    !value.href.startsWith("//") &&
    typeof value.index === "number" &&
    Number.isInteger(value.index) &&
    value.index >= 0
  )
    return { href: value.href, index: value.index };
}

/** The route owns its header content; the shell owns its placement and navigation. */
export const FocusLayoutContext = createContext<HTMLElement | null>(null);
export const useFocusHeader = () => useContext(FocusLayoutContext);

export function useFocusLayout() {
  const router = useRouter();
  const { active, location, status } = useRouterState({
    select: (state) => ({
      active: state.matches.some((match) => match.staticData.layout === "calculator-focus"),
      location: state.location,
      status: state.status,
    }),
  });
  const previous = useRef({ active, location });
  const returnTo = useRef<FocusReturn | undefined>(readFocusReturn(location.state));

  useEffect(() => {
    // During lazy loading, location can already be the destination while the
    // committed matches still belong to the origin. Record only settled visits.
    if (status !== "idle") return;
    const stored = readFocusReturn(location.state);
    if (active) {
      if (stored) {
        returnTo.current = stored;
      } else if (!previous.current.active) {
        returnTo.current = {
          href: previous.current.location.href,
          index: previous.current.location.state.__TSR_index,
        };
      }
      // Store the origin on each history entry, so refresh and browser Forward
      // preserve it without a global "last page" that can belong to another visit.
      if (returnTo.current && !stored) {
        router.history.replace(location.href, {
          ...router.history.location.state,
          calculatorReturn: returnTo.current,
        });
      }
    } else {
      returnTo.current = undefined;
    }
    previous.current = { active, location };
  }, [active, location, router, status]);

  const leave = async () => {
    const origin = returnTo.current;
    const index = router.history.location.state.__TSR_index;
    if (origin && origin.index < index) {
      // Skip mode-tab entries together, preserving the actual previous route,
      // its filters and its native Back/Forward history.
      await new Promise<void>((resolve) => {
        const unsubscribe = router.subscribe("onResolved", () => {
          unsubscribe();
          resolve();
        });
        router.history.go(origin.index - index);
      });
    } else {
      await router.navigate({ href: origin?.href ?? "/", replace: true });
    }
  };

  const exit = () => {
    if (
      document.startViewTransition &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      // Native snapshots let departing surfaces settle down without keeping
      // an old React route mounted or delaying the new route's inputs.
      document.startViewTransition(leave);
    } else {
      void leave();
    }
  };

  return { active, exit };
}
